import * as React from "react";
import { createRoot } from "react-dom/client";
import TaskPane from "./components/TaskPane";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import posthog from "posthog-js";
import { POSTHOG_CONFIG, SENTRY_CONFIG } from "../config/analytics";
import { fetchSsoToken } from "../utils/sso";
import { NavigationTracker } from "../services/navigationTracker";
import { createEventBus, createEventEmitter } from "../lib/eventing/events";
import type { NavigationEventMap } from "../services/navigationEvents";
import { initialiseSentry } from "../services/sentry";
import { initialisePosthog } from "../services/posthog";
import { ExcelEventRelay } from "../services/excelEventRelay";
import { ExcelEvents } from "../services/excelEvents";
import { WorkbookClassifier, WorkbookClassificationEvents, WorkbookClassification } from "../services/workbookClassification";
import { WorksheetClassifier, WorksheetClassificationEvents, WorksheetClassification } from "../services/worksheetClassification";
import { defineOfficeDocumentConfigStore } from "../lib/config.msoffice/officeDocumentConfigStore";
import { EventBusProvider } from "./hooks/useEventBus";
import { EventLogger } from "../services/eventLogger";
import { classificationRules } from "../data/workbookClassificationRulesDatabase";
import { workbookWorksheetClassificationRules } from "../data/workbookWorksheetClassificationRulesDatabase";
import { AlertEvents, WorkbookAnalyticsRulesEngine } from "../services/workbookAnalyticsRulesEngine";
import { workbookRulesetsByCode } from "../data/analyticsRulesDatabase";
import { ProgressiveEnhancementEvents, ProgressiveEnhancementService, createNamedRangesProcessor } from "../services/progressiveEnhancement";
import { progressiveEnhancementRules } from "../data/progressiveEnhancementRulesDatabase";
import { defineOfficeRoamingConfigStore } from "../lib/config.msoffice/officeRoamingConfigStore";
import { TaskPaneSettings } from "./components/TaskPane";
import { ArtifactEvents, ArtifactStore } from "../services/artifacts";
import { WorksheetExtractor } from "../services/worksheetExtraction";
import { workbookExtractionRules } from "../data/worksheetExtractionsDatabase";


const title = "Aμto Coach";
const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const consoleLogger = (message: string, error?: Error) => console.error(message, error);

const navigationEventEmitter = createEventEmitter<NavigationEventMap>(consoleLogger);
const excelEventEmitter = createEventEmitter<ExcelEvents>(consoleLogger);
const workbookClassificationEmitter = createEventEmitter<WorkbookClassificationEvents>(consoleLogger);
const worksheetClassificationEmitter = createEventEmitter<WorksheetClassificationEvents>(consoleLogger);
const progressiveEnhancementEmitter = createEventEmitter<ProgressiveEnhancementEvents>(consoleLogger);
const alertEventEmitter = createEventEmitter<AlertEvents>(consoleLogger);
const artifactEventEmitter = createEventEmitter<ArtifactEvents>(consoleLogger);

const eventBus = createEventBus({
    navigation: navigationEventEmitter,
    excel: excelEventEmitter,
    workbookClassification: workbookClassificationEmitter,
    worksheetClassification: worksheetClassificationEmitter,
    progressiveEnhancement: progressiveEnhancementEmitter,
    alert: alertEventEmitter,
    artifact: artifactEventEmitter
});
eventBus.on((_payload, event) =>  console.log(`Event: ${event.type}`, event));

const localRulesetFetcher = async (workbookCode: string) => {
    console.log("Fetching ruleset for workbook code:", workbookCode);
    const ruleset = workbookRulesetsByCode[workbookCode]
    console.log("Fetched ruleset:", ruleset);
    return ruleset;
};
const workbookWorksheetClasificationRuleFetcher = async (workbookCode: string) => {
    return workbookWorksheetClassificationRules[workbookCode];
}

const progressiveEnhancementRulesFetcher = async (workbookCode: string) => {
    return progressiveEnhancementRules[workbookCode];
};

const workbookExtractionRuleFetcher = async (workbookCode: string) => {
    return workbookExtractionRules[workbookCode];
}

const workbookClassificationStore = defineOfficeDocumentConfigStore<WorkbookClassification>("workbook-classification");
const worksheetClassificationStore = defineOfficeDocumentConfigStore<Record<string, WorksheetClassification>>(`worksheet-classification`);

initialiseSentry(SENTRY_CONFIG, excelEventEmitter);
initialisePosthog(POSTHOG_CONFIG, eventBus);
const excelEventSource = new ExcelEventRelay(excelEventEmitter);
const _navigationTracker = new NavigationTracker(excelEventEmitter, navigationEventEmitter);
const _workbookClassification = new WorkbookClassifier(classificationRules, excelEventEmitter, workbookClassificationEmitter, workbookClassificationStore);
const _worksheetClassification = new WorksheetClassifier(workbookWorksheetClasificationRuleFetcher, excelEventEmitter, workbookClassificationEmitter, worksheetClassificationEmitter, worksheetClassificationStore);
const _progressiveEnhancementService = new ProgressiveEnhancementService(progressiveEnhancementRulesFetcher, workbookClassificationEmitter, worksheetClassificationEmitter, progressiveEnhancementEmitter);
_progressiveEnhancementService.registerProcessor('create-named-range', createNamedRangesProcessor('_μ_'));

export const rulesEngine = new WorkbookAnalyticsRulesEngine(localRulesetFetcher, workbookClassificationEmitter, progressiveEnhancementEmitter, alertEventEmitter);
export const eventLogger = new EventLogger(eventBus);
const settingsStore = defineOfficeRoamingConfigStore<TaskPaneSettings>("ai-coach-settings");

const artifactStore = new ArtifactStore(artifactEventEmitter);
const _worksheetExtractor = new WorksheetExtractor(eventBus, artifactStore, workbookExtractionRuleFetcher);

const identifyCurrentUser = async () => {
    const tokenResult = await fetchSsoToken();
    if (!tokenResult?.claims) {
        return;
    }

    const { claims } = tokenResult;
    const distinctId = claims.oid ?? claims.preferred_username;

    if (!distinctId) {
        console.warn("SSO token did not include a usable identifier.");
        return;
    }

	excelEventEmitter.events.userIdentified.emit({
		id: distinctId,
		username: claims.name,
		email: claims.preferred_username
	});
};

/* Render application after Office initializes */
Office.onReady(async (info) => {
    if (info.host !== Office.HostType.Excel) return;
    Office.addin.setStartupBehavior(Office.StartupBehavior.load);

    await identifyCurrentUser();

    root?.render(
        <Sentry.ErrorBoundary fallback={<div>An error has occurred. Please refresh the page.</div>}>
            <PostHogProvider client={posthog}>
                <EventBusProvider eventBus={eventBus}>
                    <FluentProvider theme={webLightTheme}>
                        <TaskPane title={title} settingsStore={settingsStore} />
                    </FluentProvider>
                </EventBusProvider>
            </PostHogProvider>
        </Sentry.ErrorBoundary>
    );

    excelEventEmitter.events.appMounted.emitLoose({
        title,
        office: {
            host: Office.context.host.toString(),
            platform: Office.context.platform.toString(),
            version: Office.context.diagnostics.version
        }
    });

    await excelEventSource.start();
});
